<template>
    <tailwind-layout title="Passive Income">
        <div class="bg-white rounded-md shadow-sm border border-gray-200">
            <div class="sm:col-span-6 py-4 px-4 border-b border-gray-200">
                <h4 class="text-xl font-bold text-gray-700 sm:text-2xl">Earn Passive Income</h4>
                <p class="mt-1 max-w-3xl text-sm text-gray-500">
                    Share your RentRisk affiliate link with other landlords, agents or property managers to earn
                    passive income. When they sign up and screen applicants, you will earn 10-15% of every application.
                </p>
                <p class="mt-2 max-w-3xl text-sm text-gray-500">
                    <em>Pro tip</em>: Commenting in real estate Facebook groups can be a great way to get exposure and
                    multiple sign-ups under your link.
                </p>
            </div>
            <div class="p-6 bg-gray-50">
                <div :class="['grid grid-cols-1 gap-6', hasReachedInvitationThreshold ? '' : 'md:grid-cols-2']">
                    <div v-if="!hasReachedInvitationThreshold" class="p-6 bg-white border border-gray-200 rounded-md shadow-sm">
                        <h4 class="font-bold text-gray-700 text-xl">Send An Email</h4>
                        <jet-label for="email" value="Enter one or more email addresses, seperated by commas."
                            class="mb-2 block text-sm font-medium text-gray-700" />

                        <div class="flex flex-col items-start gap-3">
                            <jet-input id="email" type="email" 
                                class="block w-full"
                                v-model="invitationForm.email" required />
                            
                            <button type="button" 
                                @click="submitEmailInvitation" 
                                :disabled="!isButtonActive" 
                                class="primaryButton whitespace-nowrap">
                                Send Invitation
                            </button>
                        </div>
                        <p v-text="invitationForm.errors.email" class="text-red-500 text-sm mt-2"></p>
                    </div>
                    <div class="p-6 bg-white border border-gray-200 rounded-md shadow-sm">
                        <!-- Share Section -->
                        <div class="flex flex-col items-start gap-2">
                            <h4 class="font-bold text-gray-700 text-xl">Share Your Link</h4>
                            <!-- Link & Share Icons -->
                            <div class="flexflex-col md:flex-row items-center gap-3">
                                <!-- Partner Link Copy Box -->
                                <div class="relative flex items-center bg-gray-50 border border-gray-300 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition">
                                    <!-- Link Text -->
                                    <span 
                                        class="flex-1 select-all text-sm font-mono px-3 py-2 text-gray-700 cursor-pointer"
                                        @click="copyToClipboard"
                                    >
                                        {{ affiliate_slug }}
                                    </span>

                                    <!-- Copy Button -->
                                    <button 
                                        type="button" 
                                        @click="copyToClipboard" 
                                        class="flex items-center justify-center px-3 py-2 bg-white hover:bg-gray-100 border-l border-gray-300 transition"
                                    >
                                        <font-awesome-icon 
                                            :icon="copied ? faCheck : faLink" 
                                            :class="copied ? 'text-green-500' : 'text-rent-risk-blue-400'" 
                                            class="h-5 w-5 transition-colors"
                                        />
                                        <span v-if="copied" class="ml-1 text-xs">Copied</span>
                                    </button>
                                </div>

                                <div class="flex gap-4 justify-center md:justify-normal mt-4">
                                    <!-- Social Icons -->
                                    <a 
                                        :href="`https://www.facebook.com/sharer/sharer.php?u=${affiliate_slug}`"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        class="text-[#1877F2] hover:opacity-80"
                                    >
                                        <font-awesome-icon :icon="faSquareFacebook" size="2x" class="text-rent-risk-blue-400" />
                                    </a>

                                    <a 
                                        :href="`https://www.linkedin.com/sharing/share-offsite/?url=${affiliate_slug}`"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        class="text-[#0077B5] hover:opacity-80"
                                    >
                                        <font-awesome-icon :icon="faLinkedin" size="2x" class="text-rent-risk-blue-400" />
                                    </a>

                                    <a 
                                        :href="`https://wa.me/?text=Check this out%20${affiliate_slug}`"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        class="text-[#25D366] hover:opacity-80"
                                    >
                                        <font-awesome-icon :icon="faSquareWhatsapp" size="2x" class="text-rent-risk-blue-400" />
                                    </a>

                                    <!-- Opens the same agent-invitation.email endpoint as the
                                         "Send An Email" card, so it follows the same threshold. -->
                                    <button 
                                        v-if="!hasReachedInvitationThreshold"
                                        type="button" 
                                        @click="showEmailInvitationPopup = true" 
                                        class="text-red-600 hover:opacity-80"
                                    >
                                        <font-awesome-icon :icon="faEnvelope" size="2x" class="text-rent-risk-blue-400" />
                                    </button>
                                </div>
                            </div>
                            <p class="text-sm text-gray-600">
                                Copy your link to share manually or click one of these icons to share automatically:
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="mt-6 bg-white rounded-md shadow-sm border border-gray-200">
            <div class="sm:col-span-6 sm:flex justify-between items-center border-b border-gray-200 py-4 px-4">
                <div>
                    <h4 class="text-xl font-bold text-gray-700 sm:text-2xl sm:truncate">Partner Details</h4>
                    <p class="text-sm text-gray-500">Partner Since {{ user.partner_program_profile ? user.partner_program_profile.terms_accepted_at_formatted : user.created_at_formatted }}</p>
                </div>
            </div>
            <div class="p-6 bg-gray-50">
                <table class="min-w-full divide-y divide-gray-300 mb-6 shadow-sm rounded-lg overflow-hidden">
                    <thead class="bg-blue-50">
                    <tr>
                        <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                            Date
                        </th>
                        <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-center">
                            Total Applications
                        </th>
                        <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-center">
                            Deposit Amount
                        </th>
                    </tr>
                    </thead>
                    <tbody class="divide-y divide-gray-200">
                    <tr v-for="(payout, index) in payouts" :key="index"
                        class="bg-white hover:bg-yellow-50 transition">
                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                            {{ payout.invoice_date }}
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-700 text-center">
                            {{ payout.total_invoices }}
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-700 text-center">
                            {{ payout.payout_amount }}
                        </td>
                    </tr>
                    <tr v-show="!payouts.length">
                        <td colspan="3" class="px-6 py-4 whitespace-nowrap text-gray-800 text-sm font-medium text-center">
                            No Applications have been processed.
                        </td>
                    </tr>
                    </tbody>
                </table>
            </div>
        </div>
        <alert-card type="warning">
            You must complete your bank account setup within <strong>3 months</strong> of joining the Partner Program, 
            otherwise you will lose your payout eligibility. 
            <inertia-link :href="route('payment-methods.index')" class="font-bold underline">
                Click here
            </inertia-link> to view or update your bank information.
        </alert-card>
        <PartnerReportSection
            :filters="filters"
            :agents="agents"
            :affiliates="affiliates"
            :show-affiliates-dropdown="show_affiliates_dropdown"
        />
        <PartnerInvitationEmail :open="showEmailInvitationPopup" @close="showEmailInvitationPopup = false"></PartnerInvitationEmail>

    </tailwind-layout>
</template>

<script>
import Icon from '@/Shared/Icon.vue'
import TailwindLayout from '@/Layouts/TailwindLayout.vue'
import FormButton from '@/Shared/FormButton.vue'
import AlertCard from "@/Shared/AlertCard.vue";
import {FontAwesomeIcon} from "@fortawesome/vue-fontawesome";
import {faLink, faCheck, faEnvelope } from "@fortawesome/pro-solid-svg-icons";
import { faSquareFacebook, faLinkedin, faSquareWhatsapp } from '@fortawesome/free-brands-svg-icons';
import PartnerInvitationEmail from '@/Shared/PartnerInvitationEmail.vue';
import PartnerReportSection from '@/Shared/PartnerReportSection.vue';
import JetLabel from "@/Jetstream/Label.vue";
import JetInput from "@/Jetstream/Input.vue";

export default {
    components: {
        FontAwesomeIcon,
        AlertCard,
        Icon,
        FormButton,
        TailwindLayout,
        PartnerInvitationEmail,
        PartnerReportSection,
        JetLabel,
        JetInput
    },
    props: {
        affiliate_slug: String,
        affiliate_account: Object,
        affiliate_stripe_id: Boolean,
        payouts: Object,
        user: Object,
        hasReachedInvitationThreshold: Boolean,
        filters: Object,
        agents: Object,
        affiliates: [Object, Array],
        show_affiliates_dropdown: Boolean
    },
    data() {
        return {
            invitationForm: this.$inertia.form({
                email: null,
            }),

            showEmailInvitationPopup: false,
            copied: false,
            faLink,
            faCheck,
            isButtonActive: true,
            faEnvelope,
            faSquareFacebook,
            faLinkedin,
            faSquareWhatsapp 
        }
    },
    methods: {
        faLink() {
            return faLink
        },
        async copyToClipboard() {
            try {
                await navigator.clipboard.writeText(this.affiliate_slug);
                this.copied = true
                setTimeout(() => this.copied = false, 1500)
            } catch($e) {
                alert('Cannot copy');
            }
        },

        submitEmailInvitation() {
            this.isButtonActive = false;
            this.invitationForm.post(route('agent-invitation.email'), {
                onSuccess: () => {
                    this.invitationForm.reset();
                    this.isButtonActive = true;
                },
                onFinish: () => {
                    this.isButtonActive = true;
                },
            });
        }
    },
}
</script>
